import { useScreenDialogStore } from '@/features/screens/stores/screen-dialog-store';
import { useScreenSelectionStore } from '@/features/screens/stores/screen-selection-store';
import { useScreenFilterStore } from '@/features/screens/stores/screen-filter-store';
import { useAssetDialogStore } from '@/features/assets/stores/asset-dialog-store';
import { useAssetSelectionStore } from '@/features/assets/stores/asset-selection-store';
import { useAssetFilterStore } from '@/features/assets/stores/asset-filter-store';
import { usePlaylistDialogStore } from '@/features/playlists/stores/playlist-dialog-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { usePlaylistFilterStore } from '@/features/playlists/stores/playlist-filter-store';
import { useScheduleDialogStore } from '@/features/schedules/stores/schedule-dialog-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import { useScheduleSidebarStore } from '@/features/schedules/stores/schedule-sidebar-store';
import { getAssetDisplayName } from '@/features/assets/utils/asset-helpers';
import { decodeFolderPath } from '@/lib/utils';

export interface ViewContext {
  /** Current page the user is on */
  page: 'screens' | 'assets' | 'playlists' | 'schedules' | null;
  /** Current folder path from URL ?path= param */
  folderPath: string | null;
  /** Item currently being viewed/edited (dialog open or active in editor) */
  activeItem: {
    type: 'screen' | 'asset' | 'playlist' | 'schedule';
    id: string;
    name: string;
  } | null;
  /** Number of selected items in list view */
  selectedCount: number;
  /** Selected item IDs (max 20 when selectedCount > 0) */
  selectedIds?: string[];
  /** Active filters applied on the current page */
  filters?: {
    search?: string;
    status?: number[];
    tags?: string[];
    fileType?: string[];
  };
  /** Which dialog is currently open */
  activeDialog?: string;
}

function getPageFromPathname(pathname: string): ViewContext['page'] {
  if (pathname.includes('screenManagement')) return 'screens';
  if (pathname.includes('assetManagement')) return 'assets';
  if (pathname.includes('playlistManagement')) return 'playlists';
  if (pathname.includes('scheduleManagement')) return 'schedules';
  return null;
}

/**
 * Build a ViewContext snapshot from current app state.
 *
 * Uses Zustand getState() (not hooks) so it can be called from
 * the DefaultChatTransport body() callback outside React lifecycle.
 */
export function getViewContext(): ViewContext {
  const pathname = window.location.pathname;
  const page = getPageFromPathname(pathname);

  const searchParams = new URLSearchParams(window.location.search);
  const rawPath = searchParams.get('path');
  const folderPath = rawPath ? decodeFolderPath(rawPath) : null;

  let activeItem: ViewContext['activeItem'] = null;
  let selectedCount = 0;
  let selectedIds: string[] | undefined;
  let filters: ViewContext['filters'] | undefined;
  let activeDialogType: string | undefined;

  if (page === 'screens') {
    const { activeDialog, dialogContext, parentContext } = useScreenDialogStore.getState();
    const selSet = useScreenSelectionStore.getState().selectedIds;
    selectedCount = selSet.size;
    // Check dialogContext first, fall back to parentContext for child dialogs
    // (e.g., WiFi Management opened from Edit Screen stores device in parentContext)
    const device = dialogContext.device ?? (activeDialog ? parentContext.device : undefined);
    if (device) {
      activeItem = {
        type: 'screen',
        id: device._id,
        name: device.deviceName || 'Unnamed Screen',
      };
    }
    // Selected IDs (cap at 20)
    if (selSet.size > 0 && selSet.size <= 20) {
      selectedIds = [...selSet];
    }
    // Active filters
    const { searchText, status, tags } = useScreenFilterStore.getState();
    if (searchText || status.length || tags.length) {
      filters = {
        ...(searchText ? { search: searchText } : {}),
        ...(status.length ? { status } : {}),
        ...(tags.length ? { tags } : {}),
      };
    }
    if (activeDialog) activeDialogType = activeDialog;
  } else if (page === 'assets') {
    const { activeDialog, dialogContext } = useAssetDialogStore.getState();
    const selSet = useAssetSelectionStore.getState().selectedIds;
    selectedCount = selSet.size;
    if (dialogContext.asset) {
      activeItem = {
        type: 'asset',
        id: dialogContext.asset._id,
        name: getAssetDisplayName(dialogContext.asset),
      };
    }
    if (selSet.size > 0 && selSet.size <= 20) {
      selectedIds = [...selSet];
    }
    const { searchText, fileType, tags } = useAssetFilterStore.getState();
    if (searchText || fileType.length || tags.length) {
      filters = {
        ...(searchText ? { search: searchText } : {}),
        ...(fileType.length ? { fileType } : {}),
        ...(tags.length ? { tags } : {}),
      };
    }
    if (activeDialog) activeDialogType = activeDialog;
  } else if (page === 'playlists') {
    const { activeDialog, dialogContext } = usePlaylistDialogStore.getState();
    const { selectedIds: selSet, activePlaylistId } = usePlaylistSelectionStore.getState();
    selectedCount = selSet.size;
    if (dialogContext.playlist) {
      activeItem = {
        type: 'playlist',
        id: dialogContext.playlist._id,
        name: dialogContext.playlist.name || 'Unnamed Playlist',
      };
    } else if (activePlaylistId) {
      activeItem = { type: 'playlist', id: activePlaylistId, name: '' };
    }
    if (selSet.size > 0 && selSet.size <= 20) {
      selectedIds = [...selSet];
    }
    const { searchText } = usePlaylistFilterStore.getState();
    if (searchText) {
      filters = { search: searchText };
    }
    if (activeDialog) activeDialogType = activeDialog;
  } else if (page === 'schedules') {
    const { activeDialog, dialogContext } = useScheduleDialogStore.getState();
    const { checkedIds, selectedScheduleId } = useScheduleSelectionStore.getState();
    selectedCount = checkedIds.size;
    if (dialogContext.schedule) {
      activeItem = {
        type: 'schedule',
        id: dialogContext.schedule._id,
        name: dialogContext.schedule.name || 'Unnamed Schedule',
      };
    } else if (selectedScheduleId) {
      activeItem = { type: 'schedule', id: selectedScheduleId, name: '' };
    }
    if (checkedIds.size > 0 && checkedIds.size <= 20) {
      selectedIds = [...checkedIds];
    }
    const { searchText } = useScheduleSidebarStore.getState();
    if (searchText) {
      filters = { search: searchText };
    }
    if (activeDialog) activeDialogType = activeDialog;
  }

  return {
    page,
    folderPath,
    activeItem,
    selectedCount,
    ...(selectedIds ? { selectedIds } : {}),
    ...(filters ? { filters } : {}),
    ...(activeDialogType ? { activeDialog: activeDialogType } : {}),
  };
}
